import { Coins, Droplets, Gift, Layers, type LucideIcon } from "lucide-react";
import { formatBnb, formatToken } from "../lib/format";

interface SummaryCardsProps {
  tokenSymbol: string;
  tokenDecimals: number;
  tokenBalance: bigint;
  lpSymbol: string;
  lpBalance: bigint;
  holderClaimable: bigint;
  bnbClaimable: bigint;
  loading?: boolean;
}

interface CardProps {
  label: string;
  value: string;
  icon: LucideIcon;
  loading?: boolean;
  accent?: string;
}

function Card({ label, value, icon: Icon, loading, accent }: CardProps) {
  return (
    <div className="glass p-4 relative transition duration-200 hover:border-white/[0.1]">
      <div className="text-xs text-ink-dim uppercase tracking-wider font-mono">{label}</div>
      {loading ? (
        <div className="h-6 mt-2 rounded bg-white/[0.04] animate-pulse" />
      ) : (
        <div
          className={`text-lg lg:text-xl font-semibold font-mono tabular-nums mt-2 break-all leading-tight ${accent ?? "text-ink"}`}
        >
          {value}
        </div>
      )}
      <Icon
        className={`absolute top-3 right-3 w-4 h-4 ${accent ? accent + "/50" : "text-ink-dim/40"}`}
        strokeWidth={1.75}
        aria-hidden
      />
    </div>
  );
}

export function SummaryCards({
  tokenSymbol,
  tokenDecimals,
  tokenBalance,
  lpSymbol,
  lpBalance,
  holderClaimable,
  bnbClaimable,
  loading,
}: SummaryCardsProps) {
  return (
    <section
      className="grid grid-cols-2 lg:grid-cols-4 gap-3"
      aria-label="账户概览"
    >
      <Card
        label="蜂鸟余额"
        value={formatToken(tokenBalance, tokenSymbol, tokenDecimals)}
        icon={Droplets}
        loading={loading}
      />
      <Card
        label="钱包 LP"
        value={formatToken(lpBalance, lpSymbol)}
        icon={Layers}
        loading={loading}
      />
      <Card
        label="可领取 LP"
        value={formatToken(holderClaimable, "LP")}
        icon={Gift}
        loading={loading}
        accent={holderClaimable > 0n ? "text-wing" : undefined}
      />
      <Card
        label="可领取 BNB"
        value={formatBnb(bnbClaimable)}
        icon={Coins}
        loading={loading}
        accent={bnbClaimable > 0n ? "text-bnb" : undefined}
      />
    </section>
  );
}
